import { assertNonNullable } from '@disy/lib/custom-error';

import type { PathElement } from 'cadenza/api-client/repositories-api';
import type { NavigatorTreeNodeDto } from 'cadenza/repository/model/navigator-tree-node-dto';
import { isParentNode, isWorkbookNode } from 'cadenza/repository/model/navigator-tree-node-dto';
import type { WorkbookName } from 'cadenza/repository/model/workbook-name.gen';

export function getWorkbookLabelsAndPath(navigatorTree: NavigatorTreeNodeDto, workbookName: WorkbookName) {
  const path = findWorkbookPath(navigatorTree, workbookName);
  const labels = path[path.length - 1].labels ?? [];
  // strip the first and last element from the path. They are the repository and the workbook itself
  const folders = path.slice(1, path.length - 1).map((info) => info.name);
  return { labels, folders };
}

/**
 * Find the path leading to a workbook in the navigator.
 *
 * @param navigatorTree - the tree or subtree of the navigatorTree
 * @param workbookName - A string identifying the workbook, example: 'rV6Qc3ssSYi682IGv69EsQ'
 * @return path - the names and labels of the parent folders and the workbook.
 */
function findWorkbookPath(navigatorTree: NavigatorTreeNodeDto, workbookName: WorkbookName): PathElement[] {
  const path = _findWorkbookPath(navigatorTree, workbookName, []);
  assertNonNullable(path, `Workbook ${workbookName} not found in repository tree.`);
  return path;
}

function _findWorkbookPath(
  navigatorTree: NavigatorTreeNodeDto,
  workbookName: WorkbookName,
  path: PathElement[],
): PathElement[] | undefined {
  // copy to avoid shared mutation.
  const newPath = [...path];
  newPath.push({ name: navigatorTree.text, labels: navigatorTree.labels });
  if (isWorkbookNode(navigatorTree) && navigatorTree.workbookName === workbookName) {
    return newPath;
  }
  const children = isParentNode(navigatorTree) ? navigatorTree.children : [];
  const subPath = children
    .map((child) => _findWorkbookPath(child, workbookName, newPath))
    .filter((p) => p !== undefined);
  if (subPath.length > 0) {
    return subPath[0]; // flatMap
  }
}
